// ============================================================================
// FarmerRegistrationsPage — #my/registrations (Task #11).
// ============================================================================
// Logged-in farmers see their workshop history split into Upcoming and Past
// sections. For each event:
//   - Title (links to public detail page via slug)
//   - Date + format badge (in-person / online / hybrid)
//   - Status pill (registered / waitlisted / cancelled / attended / no-show)
//   - Mode (in-person or online) for hybrid events
//   - Special requests they captured at registration
//   - For upcoming online registrations: "Join meeting" CTA (magic-link)
//   - For upcoming events: "Cancel registration" with confirm
//   - For past events: per-day attendance grid (Present / Absent per day)
//
// Auth-gated via FarmerAuthContext. Bounces to #farmer-signin if no token.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, LogOut, UserCircle, CalendarDays, Mail, MapPin, Video, AlertCircle,
  ArrowRight, X as XIcon, CheckCircle2,
} from 'lucide-react';
import { api, type FarmerRegistration } from '../api';
import { useFarmerAuth } from '../farmerAuth';

export function FarmerRegistrationsPage() {
  const { token, farmer, logout } = useFarmerAuth();
  const [registrations, setRegistrations] = useState<FarmerRegistration[]>([]);
  const [counts, setCounts] = useState({ total: 0, upcoming: 0, past: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  const refresh = useCallback(() => {
    if (!token) return;
    setLoading(true); setError('');
    api.farmerRegistrations(token)
      .then((d) => { setRegistrations(d.registrations ?? []); setCounts(d.counts); })
      .catch((err) => setError(err?.message || 'Could not load your registrations.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!token) { window.location.hash = '#farmer-signin'; return; }
    refresh();
  }, [token, refresh]);

  if (!token || !farmer) return null;

  async function cancel(r: FarmerRegistration) {
    const eventDate = fmtDate(r.start_date);
    if (!confirm(`Cancel your registration for "${r.title}" on ${eventDate}? You can re-register later if seats are still open.`)) return;
    setCancellingId(r.attendee_id);
    try {
      await api.farmerCancelRegistration(token!, r.attendee_id);
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not cancel that registration.');
    } finally {
      setCancellingId(null);
    }
  }

  const upcoming = registrations.filter((r) => !r.is_past);
  const past = registrations.filter((r) => r.is_past);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <a href="#my" className="text-xs text-gray-600 hover:text-uif-green-dark inline-flex items-center gap-1">
              <UserCircle className="h-4 w-4" />
              <span className="hidden sm:inline">{farmer.full_name}</span>
            </a>
            <button
              type="button"
              onClick={() => { logout(); window.location.hash = '#'; }}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full space-y-6">
        <div>
          <a href="#my" className="text-xs text-gray-500 hover:text-gray-700">← My account</a>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">My registrations</h1>
          <p className="text-sm text-gray-600 mt-1">
            Workshops you've registered for. Online events show a Join button when it's time.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {loading && (
          <div className="p-6 text-sm text-gray-600 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your registrations…
          </div>
        )}

        {!loading && registrations.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <CalendarDays className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <h2 className="font-semibold text-gray-900">No registrations yet</h2>
            <p className="text-sm text-gray-600 mt-1">
              Browse upcoming workshops on the <a href="/" className="text-uif-green-dark hover:underline">homepage</a> and sign up — they'll appear here.
            </p>
          </div>
        )}

        {!loading && upcoming.length > 0 && (
          <Section title={`Upcoming (${upcoming.length})`}>
            {upcoming.map((r) => (
              <RegistrationCard
                key={r.attendee_id}
                r={r}
                cancelling={cancellingId === r.attendee_id}
                onCancel={() => cancel(r)}
              />
            ))}
          </Section>
        )}

        {!loading && past.length > 0 && (
          <Section title={`Past (${past.length})`}>
            {past.map((r) => (
              <RegistrationCard
                key={r.attendee_id}
                r={r}
                cancelling={false}
                onCancel={() => {}}
              />
            ))}
          </Section>
        )}
      </main>

      <footer className="border-t border-gray-200 bg-white py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-xs text-gray-500">
          Up in Farms LLC · <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">support@upinfarms.com</a>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function RegistrationCard({
  r, cancelling, onCancel,
}: {
  r: FarmerRegistration;
  cancelling: boolean;
  onCancel: () => void;
}) {
  const eventHref = r.slug ? `#e/${r.slug}` : `#event/${r.event_id}`;
  const isWaitlisted = r.registration_status === 'waitlisted';
  const isCancelled = r.registration_status === 'cancelled';
  const isAttended = r.registration_status === 'attended';
  const isNoShow = r.registration_status === 'no_show';

  return (
    <article className={
      'bg-white border rounded-lg overflow-hidden ' +
      (isCancelled ? 'border-gray-200 opacity-70' : 'border-gray-200')
    }>
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <a href={eventHref} className="text-lg font-semibold text-uif-green-dark hover:underline block">
              {r.title}
            </a>
            <div className="text-sm text-gray-600 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-3.5 w-3.5" />
                {fmtDateTime(r.start_date)}
              </span>
              <FormatPill format={r.format} mode={r.attendance_mode} />
              <StatusPill
                status={r.registration_status}
                isWaitlisted={isWaitlisted}
                isCancelled={isCancelled}
                isAttended={isAttended}
                isNoShow={isNoShow}
              />
            </div>
          </div>
        </div>

        {/* Location or online block */}
        {r.attendance_mode === 'in_person' && r.location && (
          <div className="text-sm text-gray-700 flex items-start gap-1.5">
            <MapPin className="h-4 w-4 text-uif-green flex-shrink-0 mt-0.5" />
            <span>{r.location}</span>
          </div>
        )}
        {r.attendance_mode === 'online' && (
          <div className="text-sm text-gray-700 flex items-start gap-1.5">
            <Video className="h-4 w-4 text-uif-green flex-shrink-0 mt-0.5" />
            <span>
              Online via {providerLabel(r.online_provider)}
              {r.online_meeting_id && (
                <span className="text-xs text-gray-500"> · ID {r.online_meeting_id}</span>
              )}
            </span>
          </div>
        )}

        {r.special_requests && (
          <div className="text-xs text-gray-500 italic">
            Note: {r.special_requests}
          </div>
        )}

        {/* Per-day attendance — only meaningful for past events with records */}
        {r.is_past && r.attendance.length > 0 && (
          <div className="border-t border-gray-100 pt-3">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
              Attendance
            </div>
            <div className="flex flex-wrap gap-2">
              {r.attendance.map((a) => (
                <span
                  key={a.day_index}
                  className={
                    'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ' +
                    (a.present
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-gray-50 border-gray-200 text-gray-500')
                  }
                >
                  {a.present ? <CheckCircle2 className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
                  Day {a.day_index} · {a.present ? 'Present' : 'Absent'}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        {(r.can_join || r.can_cancel) && (
          <div className="border-t border-gray-100 pt-3 flex flex-wrap gap-2 justify-end">
            {r.can_join && (
              <a
                href={`#join/${r.global_id}`}
                className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white text-sm font-semibold px-3 py-1.5 hover:bg-uif-green-dark"
              >
                <Video className="h-4 w-4" />
                Join meeting →
              </a>
            )}
            {r.can_cancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={cancelling}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <XIcon className="h-4 w-4" />}
                Cancel registration
              </button>
            )}
            <a
              href={eventHref}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Event details <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        )}
      </div>
    </article>
  );
}

// --- small UI helpers ----------------------------------------------------------

function FormatPill({ format, mode }: { format: string; mode: string }) {
  if (format === 'in_person') {
    return <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border bg-amber-50 border-amber-200 text-amber-800">In person</span>;
  }
  if (format === 'online') {
    return <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border bg-blue-50 border-blue-200 text-blue-800">Online</span>;
  }
  // hybrid
  const label = mode === 'online' ? 'Hybrid · online' : 'Hybrid · in person';
  return <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border bg-violet-50 border-violet-200 text-violet-800">{label}</span>;
}

function StatusPill({
  status, isWaitlisted, isCancelled, isAttended, isNoShow,
}: {
  status: string; isWaitlisted: boolean; isCancelled: boolean; isAttended: boolean; isNoShow: boolean;
}) {
  let cls = 'bg-emerald-50 border-emerald-200 text-emerald-800';
  let label = 'Registered';
  if (isWaitlisted) { cls = 'bg-amber-50 border-amber-200 text-amber-800'; label = 'Waitlisted'; }
  if (isCancelled) { cls = 'bg-gray-100 border-gray-200 text-gray-600'; label = 'Cancelled'; }
  if (isAttended)  { cls = 'bg-uif-green/10 border-uif-green/30 text-uif-green-dark'; label = 'Attended'; }
  if (isNoShow)    { cls = 'bg-red-50 border-red-200 text-red-700'; label = 'No show'; }
  return <span className={'text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border ' + cls}>{label}</span>;
}

function providerLabel(p: string | null): string {
  if (!p) return 'online meeting';
  return ({ zoom: 'Zoom', teams: 'Microsoft Teams', meet: 'Google Meet', webex: 'Webex', other: 'online meeting' } as Record<string, string>)[p] || 'online meeting';
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}
